import {Text, VStack} from 'platform/foundation-tw';
import {cva} from 'platform/shared-tw';

import {useId, useRef, useState, type ChangeEvent, type KeyboardEvent} from 'react';
import {useTranslation} from 'react-i18next';

const inputVariants = cva(
  'w-full px-4 py-3 text-base border rounded-lg outline-none transition-colors font-[Geist] bg-transparent',
  {
    variants: {
      hasError: {
        true: 'border-(--color-mira-red-50) focus:ring-2 focus:ring-(--color-mira-red-50)/20',
        false:
          'border-(--color-mira-gray-20) focus:border-(--color-mira-gray-60) focus:ring-2 focus:ring-(--color-mira-gray-20)/20',
      },
    },
    defaultVariants: {
      hasError: false,
    },
  }
);

const optionVariants = cva('px-4 py-2 cursor-pointer transition-colors text-base', {
  variants: {
    isHighlighted: {
      true: 'bg-(--color-mira-gray-10)',
      false: 'bg-transparent hover:bg-(--color-mira-gray-10)',
    },
  },
  defaultVariants: {
    isHighlighted: false,
  },
});

export interface ComboboxOption {
  readonly value: string;
  readonly label: string;
}

export interface ComboboxProps {
  readonly label?: string;
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly options: readonly ComboboxOption[];
  readonly placeholder?: string;
  readonly error?: string;
  readonly name?: string;
}

export function Combobox(props: ComboboxProps) {
  const {t} = useTranslation();
  const id = useId();
  const listboxId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const hasError = Boolean(props.error);

  const filteredOptions = props.options.filter((option) =>
    option.label.toLowerCase().includes(props.value.toLowerCase())
  );

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    props.onChange(event.target.value);
    setIsOpen(true);
    setHighlightedIndex(-1);
  };

  const handleOptionSelect = (option: ComboboxOption) => {
    props.onChange(option.value);
    setIsOpen(false);
    setHighlightedIndex(-1);
    inputRef.current?.focus();
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (!isOpen && event.key === 'ArrowDown') {
      setIsOpen(true);
      setHighlightedIndex(0);
      event.preventDefault();
      return;
    }

    if (!isOpen) {
      return;
    }

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        setHighlightedIndex((prev) => (prev < filteredOptions.length - 1 ? prev + 1 : prev));
        break;
      case 'ArrowUp':
        event.preventDefault();
        setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : prev));
        break;
      case 'Enter':
        event.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < filteredOptions.length) {
          handleOptionSelect(filteredOptions[highlightedIndex]);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setHighlightedIndex(-1);
        break;
    }
  };

  const handleBlur = () => {
    // Delay to allow click on option to fire
    setTimeout(() => {
      setIsOpen(false);
      setHighlightedIndex(-1);
    }, 150);
  };

  return (
    <VStack spacing={2}>
      {props.label && (
        <label htmlFor={id}>
          <Text size="small" color="secondary">
            {props.label}
          </Text>
        </label>
      )}
      <div className="relative">
        <input
          ref={inputRef}
          id={id}
          name={props.name}
          type="text"
          role="combobox"
          aria-expanded={isOpen}
          aria-controls={listboxId}
          aria-activedescendant={
            highlightedIndex >= 0 ? `${listboxId}-option-${highlightedIndex}` : undefined
          }
          autoComplete="off"
          value={props.value}
          onChange={handleInputChange}
          onFocus={() => setIsOpen(true)}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          placeholder={props.placeholder}
          className={inputVariants({hasError})}
          aria-invalid={hasError}
          aria-describedby={hasError ? `${id}-error` : undefined}
        />
        <button
          type="button"
          tabIndex={-1}
          className="absolute inset-y-0 right-0 flex items-center pr-3"
          onClick={() => {
            setIsOpen(!isOpen);
            inputRef.current?.focus();
          }}
          aria-label={t('ui.combobox.toggleOptions')}
        >
          <svg
            className="h-5 w-5 text-(--color-mira-gray-60)"
            viewBox="0 0 20 20"
            fill="currentColor"
            aria-hidden="true"
          >
            <path
              fillRule="evenodd"
              d="M5.22 8.22a.75.75 0 0 1 1.06 0L10 11.94l3.72-3.72a.75.75 0 1 1 1.06 1.06l-4.25 4.25a.75.75 0 0 1-1.06 0L5.22 9.28a.75.75 0 0 1 0-1.06Z"
              clipRule="evenodd"
            />
          </svg>
        </button>
        {isOpen && filteredOptions.length > 0 && (
          <ul
            id={listboxId}
            role="listbox"
            className="absolute z-10 mt-1 w-full max-h-60 overflow-auto rounded-lg border border-(--color-mira-gray-20) bg-white shadow-lg"
          >
            {filteredOptions.map((option, index) => (
              <li
                key={option.value}
                id={`${listboxId}-option-${index}`}
                role="option"
                aria-selected={highlightedIndex === index}
                className={optionVariants({isHighlighted: highlightedIndex === index})}
                onMouseDown={() => handleOptionSelect(option)}
                onMouseEnter={() => setHighlightedIndex(index)}
              >
                {option.label}
              </li>
            ))}
          </ul>
        )}
      </div>
      {props.error && (
        <Text id={`${id}-error`} size="small" color="danger">
          {props.error}
        </Text>
      )}
    </VStack>
  );
}
